import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { Col } from 'reactstrap';
import { useForm } from 'react-hook-form';
import {
  FiChevronDown,
  FiChevronRight,
  FiFilter,
  FiShare
} from 'react-icons/fi';
import { useSelector } from 'react-redux';
import { Button, Form } from 'reactstrap';
import styled from 'styled-components';

import {
  ImageButton,
  ImageSize
} from '../../../../../../../../assets/styles/image_style';
import Loader from 'react-loader-spinner';

import xlsxImage from '../../../../../../../../assets/img/xlsx.png';
import csvImage from '../../../../../../../../assets/img/csv.png';
import { Tooltip } from '@material-ui/core';
import { saveAs } from 'file-saver';
import { toast } from 'react-toastify';

import ContactService from '../../../../../services/contactService.js';

import CustomFieldsFilterForm from 'views/CRM/shared/components/CustomFieldsFilterForm';
import DefaultFieldsFilterForm from './DefaultFieldsFilterForm';
import { buttonStyle } from 'components/Containers/ConfigurationContainer/components/utils';
import { customFieldTypes } from 'views/fieldConfigs/utils/customFieldTypes';
import { parseIntFromString } from 'utils/fuctions/parseIntFromString';
import { normalizeCpfCnpjFields } from 'utils/functions/normalizeCpfCnpj';
import normalizePhoneNumber from 'utils/fuctions/normalizePhoneNumber';
import { resetToFirstDayOfMonth } from 'views/CRM/shared/helpers/getFirstDayOfTheMonth';
import { resetFields } from 'views/CRM/shared/helpers/resetInputsFIeldsValues';
import { ClearFiltersButton } from 'components/ClearFiltersButton';
import { useMemorizeFilters } from 'hooks/useMemorizeInputsFilters';
import { POSSIBLE_FILTERS_ENTITIES } from 'hooks/useMemorizeInputsFilters';
import ExportContactsModal from '../SelectCampaignToExportModal/index';
import { useListContacts } from '../../../../../hooks/useListContacts';
import extractPhoneNumber from '../../utils/extractPhoneNumber';

const pageKey = 'flexTableConfig::[contact]';

const CustomFieldsFilterToggle = styled.div`
  display: inline-block;
  margin-bottom: 1rem;
  user-select: none;
  :hover {
    cursor: pointer;
    text-decoration: underline;
  }
`;

const FilterContactsForm = ({
  handleSearch,
  customFields,
  isLoading,
  selectedRecordTypeId,
  setSelectedRecordType,
  recordTypes,
  recordTypeId,
  totalContacts,
  contacts,
  sortConfig
}) => {
  const [showCustomFieldsFilters, setShowCustomFieldsFilters] = useState(false);
  const [showDateForm, setShowDateForm] = useState(false);
  const [analyticalData, setAnalyticalData] = useState([]);
  const [loadingCSV, setLoadingCSV] = useState(false);
  const [loadingXLSX, setLoadingXLSX] = useState(false);
  const [account, setAccount] = useState(null);
  const [isImportModalOpen, setIsImportModalOpen] = useState(false);
  const { getAllContacts, allContacts } = useListContacts({
    recordTypeId: selectedRecordTypeId,
    sortConfig
  });

  const [isExporting, setIsExporting] = useState(false);

  const { getMemorizedFilters, memorizeFilters, clearMemorizedFilters } =
    useMemorizeFilters(POSSIBLE_FILTERS_ENTITIES.CRM_CONTACT);

  const { setValue, control, getValues, handleSubmit, register, reset, watch } =
    useForm();
  const systemConfiguration = useSelector((state) => state.systemConfiguration);

  useEffect(() => {
    const filters = getMemorizedFilters();
    if (filters) {
      reset(filters);
    } else if (customFields) {
      customFields.forEach((field) => {
        if (field && field.type === customFieldTypes.DATE) {
          setValue(field.name, { DateBegin: '', DateEnd: '' });
          return;
        } else if (field) {
          setValue(field.name, '');
        }
      });
    }
  }, [
    customFields,
    getMemorizedFilters,
    reset,
    resetConfig,
    setValue,
    showCustomFieldsFilters
  ]);

  useEffect(() => {
    if (totalContacts) {
      setAnalyticalData(totalContacts);
    }
  }, [contacts]);

  useEffect(() => {
    const filters = getMemorizedFilters();
    if (!filters) return;
    handleSearch(filters);
    setShowDateForm(filters?.showDateForm || false);
    setAccount(filters?.account);
  }, []);

  useEffect(() => {
    if (isExporting && allContacts.length > 0) {
      setIsImportModalOpen(true);
      setIsExporting(false);
    }
  }, [isExporting, allContacts]);

  const getAllContactsExport = useCallback(async () => {
    const filters = getMemorizedFilters();
    try {
      setIsExporting(true);
      await getAllContacts(filters);
    } catch (error) {
      console.error('Failed to fetch contacts for export:', error);
      toast.error('Erro ao buscar contatos para exportação. Tente novamente.');
      setIsExporting(false);
    }
  }, [getAllContacts]);

  const toISOFormat = useCallback((dateTimeString, bar = false) => {
    const [date] = dateTimeString.split(' ');
    const [DD, MM, YYYY] = date.split('/');
    if (bar) return `${YYYY}/${MM}/${DD}`;
    return `${DD}/${MM}/${YYYY}`;
  }, []);

  const saveFiltersWithRecordType = (filters) => {
    const filtersWithRecordType = {
      ...filters,
      recordTypeId: selectedRecordTypeId,
      showDateForm,
      page: 1
    };
    memorizeFilters(filtersWithRecordType);
  };

  const onSubmit = async (data) => {
    if (showDateForm === false) {
      delete data.dateBegin;
      delete data.dateEnd;
      delete data.filterDateBy;
    }
    if (customFields && showCustomFieldsFilters) {
      customFields.forEach((field) => {
        if (field.type === customFieldTypes.LIST && !field.isReference) {
          const possibleValues = field.options.map((value) => value.value);
          if (!possibleValues.includes(data[field.name])) {
            data[field.name] = '';
          }
        }
      });
    }
    if (account && account !== null) {
      data.accountIds = account.value;
      data.account = account;
    }

    saveFiltersWithRecordType(data);
    const currencyFieldsName =
      customFields &&
      customFields
        .filter((field) => field.type === customFieldTypes.CURRENCY)
        .map((field) => field.name);

    let formattedData = { ...data };

    Object.keys(data).forEach((key) => {
      if (currencyFieldsName.includes(key)) {
        if (data[key]) {
          formattedData[key] = parseIntFromString(data[key]);
        }
      }
    });

    formattedData = normalizeCpfCnpjFields(formattedData, data, customFields);

    // Normalizar telefones: enviar como objeto {ddi, number} com número apenas dígitos
    if (customFields && customFields.length) {
      const phoneFields = customFields
        .filter((f) => f.type === customFieldTypes.TELEPHONE)
        .map((f) => f.name);

      phoneFields.forEach((fieldName) => {
        const val = data[fieldName];
        if (!val) return;
        formattedData[fieldName] = normalizePhoneNumber(val);
      });
    }

    if (data?.tags) {
      formattedData = {
        ...formattedData,
        tags: data?.tags?.map((tag) => tag.value)
      };
    }

    await handleSearch(formattedData);
  };

  const resetConfig = useMemo(
    () => ({
      dateBegin: resetToFirstDayOfMonth,
      dateEnd: () => new Date().toLocaleDateString('pt-br').toString(),
      filterDateBy: () => 'creation',
      createdByLogin: () => '',
      textQuery: () => '',
      tags: () => []
    }),
    []
  );

  const handleReset = (e) => {
    e.preventDefault();
    clearMemorizedFilters();
    resetFields(resetConfig, customFields, control);
    if (selectedRecordTypeId && recordTypes && recordTypes.length) {
      setSelectedRecordType(recordTypes[0]);
    }
    setShowDateForm(false);
    setAccount(null);
  };

  const formatColumnName = (name) => {
    return name.replace(/([A-Z])/g, '_$1').toLowerCase();
  };

  const handleReportExport = (typeExport, e) => {
    e.preventDefault();

    const params = getValues();

    const columnConfig = JSON.parse(localStorage.getItem(pageKey)) || [];
    const orderByConfig = columnConfig.find((col) => col.isSort);
    const orderBy = orderByConfig
      ? formatColumnName(orderByConfig.key)
      : 'created_at';
    const orderType = orderByConfig
      ? orderByConfig.sortDirection === 'ascending'
        ? 'ASC'
        : 'DESC'
      : null;
    const hiddenColumns = (columnConfig || [])
      .filter((col) => !col.visible)
      .map((col) => formatColumnName(col.key));

    const columnsConfig = localStorage.getItem(pageKey);

    const columnsOrder = columnsConfig
      ? JSON.parse(columnsConfig).map((col) => formatColumnName(col.key))
      : [];

    const dto = {
      ...params,
      dateStart: toISOFormat(params.dateBegin),
      dateEnd: toISOFormat(params.dateEnd),
      recordTypeId,
      orderBy,
      orderType,
      hiddenColumn: hiddenColumns,
      columnsOrder: encodeURIComponent(columnsOrder)
    };

    if (showDateForm === false) {
      delete dto.dateBegin;
      delete dto.dateEnd;
      delete dto.filterDateBy;
    }
    if (dto?.textQuery) {
      dto.textQuery = extractPhoneNumber(dto.textQuery);
    }

    if (params && params.tags) {
      dto.tags = params.tags.map((tag) => tag.value);
    }

    if (typeExport === 'XLSX') {
      setLoadingXLSX(true);

      ContactService.getExcel(dto)
        .then((res) => {
          saveAs(
            new Blob([res.data], {
              type: 'application/octet-stream'
            }),
            `relatorio-contatos.xlsx`
          );

          setLoadingXLSX(false);
        })
        .catch(() => {
          setLoadingXLSX(false);
          toast.info('Nenhum registro foi encontrado!');
        });
    } else if (typeExport === 'CSV') {
      setLoadingCSV(true);

      ContactService.getCsv(dto)
        .then((res) => {
          if (res.data.size === 0) {
            setLoadingCSV(false);
            toast.info('Nenhum registro foi encontrado!');
          } else {
            saveAs(
              new Blob([res.data], { type: 'application/octet-stream' }),
              `relatorio-contatos.csv`
            );

            setLoadingCSV(false);
          }
        })
        .catch(() => {
          setLoadingCSV(false);
          toast.info('Nenhum registro foi encontrado!');
        });
    }
  };

  return (
    <Form onSubmit={handleSubmit(onSubmit)}>
      <ClearFiltersButton
        styles={{ position: 'absolute', right: '1rem', top: '2rem' }}
        handleOnClick={handleReset}
      />
      <DefaultFieldsFilterForm
        handleSearch={handleSearch}
        setValue={setValue}
        control={control}
        getValues={getValues}
        register={register}
        showDateForm={showDateForm}
        setShowDateForm={setShowDateForm}
        account={account}
        setAccount={setAccount}
      />

      <ExportContactsModal
        isModalOpen={isImportModalOpen}
        onModalToggle={setIsImportModalOpen}
        contacts={allContacts}
      />

      {customFields && customFields.length > 0 && (
        <>
          <CustomFieldsFilterToggle
            className="small"
            onClick={() => setShowCustomFieldsFilters(!showCustomFieldsFilters)}
          >
            Filtrar por campos customizados{' '}
            {showCustomFieldsFilters ? (
              <FiChevronDown size={18} />
            ) : (
              <FiChevronRight size={18} />
            )}
          </CustomFieldsFilterToggle>

          {showCustomFieldsFilters && (
            <CustomFieldsFilterForm
              toISOFormat={toISOFormat}
              getValues={getValues}
              watch={watch}
              customFields={customFields}
              setValue={setValue}
              control={control}
            />
          )}
        </>
      )}

      <div>
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
          }}
        >
          <div style={{ display: 'flex', gap: '10px' }}>
            <Button
              style={buttonStyle(systemConfiguration.primary_color)}
              type="submit"
              size="md"
              disabled={isLoading}
            >
              <FiFilter />
              <span className="ml-1">Filtrar Contatos</span>
            </Button>

            <Button
              style={buttonStyle(systemConfiguration.primary_color)}
              type="button"
              size="md"
              onClick={async () => await getAllContactsExport()}
            >
              <FiShare />
              <span className="ml-1">Exportar para campanha</span>
            </Button>
          </div>
          <Col
            md="6"
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'flex-end',
              gap: '10px'
            }}
          >
            <button
              id="button_csv"
              style={ImageButton}
              onClick={(e) => {
                handleReportExport('CSV', e);
              }}
              disabled={analyticalData && !loadingCSV ? false : true}
            >
              <Tooltip
                title={
                  <p style={{ fontSize: '14px' }}>
                    {analyticalData
                      ? `Fazer download do relatório de Contatos em CSV.`
                      : 'Nenhum registro para realizar download'}
                  </p>
                }
                arrow
              >
                {loadingCSV ? (
                  <Loader
                    type="ThreeDots"
                    visible={true}
                    height="40"
                    width="40"
                  />
                ) : (
                  <img src={csvImage} alt="csv" style={ImageSize} />
                )}
              </Tooltip>
            </button>
            <button
              id="button_xlsx"
              style={ImageButton}
              onClick={(e) => {
                handleReportExport('XLSX', e);
              }}
              disabled={analyticalData && !loadingXLSX ? false : true}
            >
              <Tooltip
                title={
                  <p style={{ fontSize: '14px' }}>
                    {analyticalData
                      ? `Fazer download do relatório de Contatos em XLSX.`
                      : 'Nenhum registro para realizar download'}
                  </p>
                }
                arrow
              >
                {loadingXLSX ? (
                  <Loader
                    type="ThreeDots"
                    visible={true}
                    height="40"
                    width="40"
                  />
                ) : (
                  <img src={xlsxImage} alt="xlsx" style={ImageSize} />
                )}
              </Tooltip>
            </button>
          </Col>
        </div>
        <div>
          <div style={{ fontSize: 'small', float: 'right' }}>
            {totalContacts} resultado(s) encontrado(s)
          </div>
        </div>
      </div>
    </Form>
  );
};

export default FilterContactsForm;
